import { useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthError, isChildBand, scorePassword, type RegistrationInput } from '@yuan-math/auth';
import type { AgeBand } from '@yuan-math/learning';
import { Button, Card } from '@yuan-math/ui';
import { PasswordField } from '../components/PasswordField.tsx';
import { useAuth } from '../state/AuthContext.tsx';
import { useLearner } from '../state/LearnerContext.tsx';

/**
 * Registration.
 *
 * Asks for the minimum the platform can function on (§29): an email, a chosen
 * name, an age band, and — for a child — a guardian's address. No birthday, no
 * school, no location, and nothing optional dressed up as required.
 */
const BANDS: { id: AgeBand; label: string }[] = [
  { id: 'kindergarten', label: 'Kindergarten' },
  { id: 'elementary', label: 'Elementary school' },
  { id: 'middle', label: 'Middle school' },
  { id: 'high', label: 'High school' },
  { id: 'university', label: 'University' },
  { id: 'adult', label: 'An adult learning' },
];

/**
 * Typing a ten-character password blind and getting one key wrong locks you out
 * of an account you have just made, and the only way back is the reset email.
 * So it is asked for twice, and the two are compared here — before the provider
 * is ever called, like every other rule on this form.
 */
const PASSWORDS_DIFFER = 'The two passwords do not match.';

export function RegisterPage(): JSX.Element {
  const { register, configured, signedIn, ready } = useAuth();
  const { profile, createLearner } = useLearner();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
    passwordConfirmation: '',
    displayName: profile?.displayName ?? '',
    ageBand: (profile?.ageBand === 'unknown' ? 'elementary' : profile?.ageBand) ?? ('elementary' as AgeBand),
    guardianEmail: '',
    acceptedTerms: false,
  });
  const [error, setError] = useState<AuthError | null>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const needsGuardian = isChildBand(form.ageBand as AgeBand);
  const strength = form.password === '' ? null : scorePassword(form.password);
  // Only once there is something to compare: complaining at the first keystroke
  // of the second box says "wrong" to someone who is simply not finished.
  const mismatch = form.passwordConfirmation !== '' && form.passwordConfirmation !== form.password;
  const fieldError = (field: string): string | null => (error?.field === field ? error.message : null);

  if (!configured) return <AccountsUnavailable />;

  // Until the session has been looked up, "not signed in" is not yet a fact.
  // Offering the form on it invites somebody to register the account they are
  // already holding, and the answer they get back is "that email is taken".
  if (!ready) return <div className="route-loading" aria-busy="true" />;

  if (signedIn) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">You are already signed in</h1>
        <div className="button-row">
          <Link to="/home" className="btn btn--primary">Go to my home</Link>
          <Link to="/account" className="btn btn--secondary">Account settings</Link>
        </div>
      </div>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setConfirmationError(null);

    if (form.password !== form.passwordConfirmation) {
      setConfirmationError(PASSWORDS_DIFFER);
      return;
    }

    setBusy(true);
    try {
      const input: RegistrationInput = {
        email: form.email,
        password: form.password,
        displayName: form.displayName,
        ageBand: form.ageBand as AgeBand,
        acceptedTerms: form.acceptedTerms,
        ...(needsGuardian ? { guardianEmail: form.guardianEmail } : {}),
      };
      await register(input);

      // Keep any progress made as a guest by creating the local learner now.
      if (!profile) createLearner({ displayName: form.displayName, ageBand: form.ageBand as AgeBand });

      navigate(`/activate?sent=${encodeURIComponent(form.email)}`);
    } catch (cause) {
      setError(cause instanceof AuthError ? cause : new AuthError('not-supported', 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--narrow">
      <h1 className="page__title">Create an account</h1>
      <p className="page__lead">
        An account keeps your progress across devices. You can keep learning without one — nothing on this site is
        locked behind registration.
      </p>

      <form className="start-form" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field__label">Your name</span>
          <input
            className="field__input"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            maxLength={40}
            autoComplete="nickname"
          />
          <span className="field__hint">A nickname is fine. We do not need your real name.</span>
          {fieldError('displayName') ? <span className="field__error">{fieldError('displayName')}</span> : null}
        </label>

        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="field__input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            autoComplete="email"
          />
          {fieldError('email') ? <span className="field__error">{fieldError('email')}</span> : null}
          {error?.code === 'email-taken' ? (
            <span className="field__hint">
              <Link to="/signin">Sign in</Link> — or{' '}
              <Link to={`/activate?sent=${encodeURIComponent(form.email.trim())}`}>finish activating</Link> that
              account.
            </span>
          ) : null}
        </label>

        <PasswordField
          label="Password"
          value={form.password}
          onChange={(password) => {
            setConfirmationError(null);
            setForm({ ...form, password });
          }}
          autoComplete="new-password"
        >
          {strength ? (
            <>
              <span className="strength" aria-live="polite">
                <span className={`strength__bar strength__bar--${strength.score}`} />
                <span className="strength__label">
                  {['Too weak', 'Weak', 'Reasonable', 'Strong', 'Very strong'][strength.score]}
                </span>
              </span>
              {strength.suggestions[0] ? <span className="field__hint">{strength.suggestions[0]}</span> : null}
            </>
          ) : (
            <span className="field__hint">
              At least 10 characters. A short phrase is stronger and easier to remember than a jumble.
            </span>
          )}
          {fieldError('password') ? <span className="field__error">{fieldError('password')}</span> : null}
        </PasswordField>

        <PasswordField
          label="Type your password again"
          value={form.passwordConfirmation}
          onChange={(passwordConfirmation) => {
            setConfirmationError(null);
            setForm({ ...form, passwordConfirmation });
          }}
          autoComplete="new-password"
        >
          {mismatch || confirmationError ? (
            <span className="field__error" role="alert">{PASSWORDS_DIFFER}</span>
          ) : (
            <span className="field__hint">So a typo does not lock you out of a brand new account.</span>
          )}
        </PasswordField>

        <label className="field">
          <span className="field__label">What describes you?</span>
          <select
            className="field__input"
            value={form.ageBand}
            onChange={(e) => setForm({ ...form, ageBand: e.target.value as AgeBand })}
          >
            {BANDS.map((band) => (
              <option key={band.id} value={band.id}>{band.label}</option>
            ))}
          </select>
        </label>

        {needsGuardian ? (
          <label className="field">
            <span className="field__label">A parent or guardian’s email</span>
            <input
              className="field__input"
              type="email"
              value={form.guardianEmail}
              onChange={(e) => setForm({ ...form, guardianEmail: e.target.value })}
              autoComplete="email"
            />
            <span className="field__hint">
              We will ask them to confirm before the account is used. It must be a different address from the
              learner’s.
            </span>
            {fieldError('guardianEmail') ? <span className="field__error">{fieldError('guardianEmail')}</span> : null}
          </label>
        ) : null}

        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={form.acceptedTerms}
            onChange={(e) => setForm({ ...form, acceptedTerms: e.target.checked })}
          />
          <span>
            I accept the terms and the <Link to="/privacy">privacy notice</Link>.
          </span>
        </label>
        {fieldError('terms') ? <span className="field__error">{fieldError('terms')}</span> : null}

        {error && !error.field ? <p className="form-error" role="alert">{error.message}</p> : null}

        <Button type="submit" size="lg" disabled={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
        </Button>

        <p className="field__hint">
          Already have an account? <Link to="/signin">Sign in</Link>.
        </p>
      </form>
    </div>
  );
}

export function AccountsUnavailable(): JSX.Element {
  return (
    <div className="page page--narrow">
      <h1 className="page__title">Accounts are not switched on here</h1>
      <p className="page__lead">
        This deployment has no authentication service configured, so accounts cannot be created yet.
      </p>
      <Card>
        <p>
          Everything else works. Your progress is saved in this browser, and the whole curriculum — lessons, the Math
          Map, practice and the placement test — is available without signing in.
        </p>
      </Card>
      <div className="button-row">
        <Link to="/start" className="btn btn--primary">Continue without an account</Link>
        <Link to="/map" className="btn btn--secondary">Explore the Math Map</Link>
      </div>
    </div>
  );
}
